import { findIndex } from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Subject, Subscription, combineLatest, interval, map, takeUntil, takeWhile } from 'rxjs';

import { MobileQuestsService, MobileThemeService, ToasterService } from '@sifca-monorepo/clients';
import { GameTimerEnum, MemorySetupEnum, QuestActivityType } from '@sifca-monorepo/widget-generator';

import { MemoryCard } from './memory-card';
import { GameService } from './game.service';

@Component({
  template: '',
})
export class MobileBoardComponent implements OnInit {
  private subscription: Subscription;
  private unsubscribeAll: Subject<any> = new Subject<any>();

  @Output() performActivity = new EventEmitter<any>();
  @Output() argsUpdate = new EventEmitter<any>();

  darkMode;
  timer: number;
  gridColumns = 4;
  timerOn: boolean;
  timeLeft: number;
  doneMoves: number;
  cardCount: number;
  boardWidth: number;
  obs = interval(1000);
  displayedTime: string;
  isButtonDisabled = true;
  isLastActivity: boolean;
  cards: MemoryCard[] = [];
  remainingCardPairs: number;
  questActivities: QuestActivityType[];
  selectedQuestActivity: QuestActivityType;
  activity$ = this.questsService.selectedQuestActivity$;

  constructor(
    private cd: ChangeDetectorRef,
    private gameService: GameService,
    private translate: TranslateService,
    private toasterService: ToasterService,
    private themeService: MobileThemeService,
    private questsService: MobileQuestsService,
  ) {}

  ngOnInit(): void {
    this.questsService.questActivitesByQuest$.pipe(takeUntil(this.unsubscribeAll)).subscribe((questActivitesByQuest) => {
      this.questActivities = questActivitesByQuest;
    });
    this.questsService.selectedQuestActivity$.pipe(takeUntil(this.unsubscribeAll)).subscribe((questActivitesByQuest) => {
      this.selectedQuestActivity = questActivitesByQuest;
      if (questActivitesByQuest) {
        this.isLastActivity = this.selectedQuestActivity?.id === this.questActivities[this.questActivities?.length - 1]?.id;
      }
      this.cardCount =
        questActivitesByQuest?.activity?.action?.definition?.game?.memory?.setup === MemorySetupEnum.EIGHT
          ? 8
          : (this.cardCount =
              questActivitesByQuest?.activity?.action?.definition?.game?.memory?.setup === MemorySetupEnum.FOUR
                ? 4
                : (this.cardCount = questActivitesByQuest?.activity?.action?.definition?.game?.memory?.setup === MemorySetupEnum.SIXTEEN ? 16 : 12));
      this.gridColumns = this.resolveGridColumns(this.cardCount);
      this.cd.markForCheck();
    });
    this.gameService.getRemainingCardPairs().subscribe((remainingCardPairs) => {
      if (remainingCardPairs === 0 && this.cards?.length) {
        this.isButtonDisabled = false;
        this.subscription?.unsubscribe();
        this.navigateNextSlide();
      }
      return (this.remainingCardPairs = remainingCardPairs);
    });
    this.gameService.getDoneMoves().subscribe((r) => (this.doneMoves = r));
    this.themeService.darkMode$.pipe(takeUntil(this.unsubscribeAll)).subscribe((darkMode) => {
      this.darkMode = darkMode;
      this.cd.detectChanges();
    });
    this.newGame();
  }

  private resolveGridColumns(count: number): number {
    if (count <= 4) {
      return 2;
    }
    if (count <= 6) {
      return 3;
    }
    return 4;
  }

  play() {
    this.subscription?.unsubscribe();
    this.timerOn = true;
    this.timer = this.timeLeft =
      this.selectedQuestActivity?.activity?.action?.definition?.game?.memory?.timer === GameTimerEnum.FIVE_MINUTES
        ? 300
        : this.selectedQuestActivity?.activity?.action?.definition?.game?.memory?.timer === GameTimerEnum.HALF_MINUTE
        ? 30
        : this.selectedQuestActivity?.activity?.action?.definition?.game?.memory?.timer === GameTimerEnum?.MINUTE
        ? 60
        : 120;
    this.subscription = this.obs.pipe(takeWhile(() => this.timerOn)).subscribe(() => {
      this.timeLeft--;
      this.displayedTime = this.displayTime(this.timeLeft);
      if (this.timeLeft === 0) {
        combineLatest([this.translate.get('FAILURE_MSG'), this.translate.get('REPLAY_GAME')])
          .pipe(
            map(([failed, tryAgain]) => {
              this.toasterService.error(failed, tryAgain);
            }),
          )
          .subscribe();
        this.timerOn = false;
        this.subscription?.unsubscribe();
      }
    });
  }

  restartGame() {
    this.timerOn = false;
    this.timeLeft = -1;
    this.displayedTime = '';
    this.subscription?.unsubscribe();
    this.newGame();
  }

  private displayTime(time: number) {
    let min = Math.floor(time / 60);
    let sec = time % 60;
    return sec < 10 ? min.toString() + ':0' + sec.toString() : min.toString() + ':' + sec.toString();
  }

  navigateNextSlide() {
    const index = findIndex(this.questActivities, (qst) => qst?.id === this.selectedQuestActivity?.id);
    const game = {
      index,
      game: {
        activity: this.selectedQuestActivity?.id,
        movesCount: this.doneMoves,
        duration: this.timer - this.timeLeft,
      },
    };
    if (this.isLastActivity) {
      this.performActivity.emit(game);
    } else {
      let index = findIndex(this.questActivities, (item) => item?.id === this.selectedQuestActivity?.id);
      this.questsService.selectedQuestActivity$ = this.questActivities[index + 1];
      this.argsUpdate.emit(game);
    }
  }

  newGame() {
    this.isButtonDisabled = true;
    this.gameService.getCards(this.cardCount).subscribe((r) => {
      this.cards = r;
      setTimeout(() => {
        this.play();
      }, 3000);
    });
  }

  ngOnDestroy(): void {
    this.cards = [];
    this.timerOn = true;
    this.timeLeft = -1;
    this.subscription?.unsubscribe();
    this.unsubscribeAll.next(true);
    this.unsubscribeAll.complete();
  }
}
